import { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (place: PlacePredictionOption | null) => void;
  placeholder: string;
  helperText?: string;
  disabled?: boolean;
  restrictToCities?: boolean;
  restrictToEstablishments?: boolean;
  regionContext?: string;
}

interface PlacePredictionOption {
  placeId: string;
  description: string;
  coordinates?: {
    lat: number;
    lng: number;
  } | null;
}

interface GooglePlacePrediction {
  description: string;
  place_id: string;
  types?: string[];
}

interface GoogleAutocompleteService {
  getPlacePredictions(
    request: {
      input: string;
      sessionToken?: unknown;
      types?: string[];
    },
    callback: (
      predictions: GooglePlacePrediction[] | null,
      status: string
    ) => void
  ): void;
}

interface GooglePlaceDetailsLocation {
  lat(): number;
  lng(): number;
}

interface GooglePlaceDetailsResult {
  geometry?: {
    location?: GooglePlaceDetailsLocation;
  };
}

interface GooglePlacesService {
  getDetails(
    request: {
      placeId: string;
      fields: string[];
      sessionToken?: unknown;
    },
    callback: (
      place: GooglePlaceDetailsResult | null,
      status: string
    ) => void
  ): void;
}

type GoogleMapsWindow = Window & {
  google?: {
    maps?: {
      places?: {
        AutocompleteService?: new () => GoogleAutocompleteService;
        AutocompleteSessionToken?: new () => unknown;
        PlacesService?: new (attrContainer: Element) => GooglePlacesService;
        PlacesServiceStatus?: {
          OK?: string;
        };
      };
    };
  };
};

type AvailabilityState =
  | 'loading'
  | 'ready'
  | 'missing_api_key'
  | 'script_load_failed'
  | 'script_missing_library';

const GOOGLE_MAPS_SCRIPT_ID = 'matcha-google-maps-places-script';
const PLACES_LIBRARY_READY_TIMEOUT_MS = 5000;
const PLACES_LIBRARY_POLL_INTERVAL_MS = 50;
let loadPlacesApiPromise: Promise<void> | null = null;

function getMapsWindow(): GoogleMapsWindow | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window as GoogleMapsWindow;
}

function isPlacesLibraryReady(): boolean {
  const mapsWindow = getMapsWindow();
  return Boolean(mapsWindow?.google?.maps?.places?.AutocompleteService);
}

function waitForPlacesLibraryReady(
  timeoutMs: number = PLACES_LIBRARY_READY_TIMEOUT_MS
): Promise<void> {
  if (isPlacesLibraryReady()) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const mapsWindow = getMapsWindow();
    if (!mapsWindow) {
      reject(new Error('Google Places API can only be loaded in the browser.'));
      return;
    }

    const startedAt = Date.now();
    const poll = () => {
      if (isPlacesLibraryReady()) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Google Places library is not available.'));
        return;
      }

      mapsWindow.setTimeout(poll, PLACES_LIBRARY_POLL_INTERVAL_MS);
    };

    poll();
  });
}

function loadGooglePlacesApi(apiKey: string): Promise<void> {
  if (isPlacesLibraryReady()) {
    return Promise.resolve();
  }

  if (loadPlacesApiPromise) {
    return loadPlacesApiPromise;
  }

  loadPlacesApiPromise = new Promise<void>((resolve, reject) => {
    const mapsWindow = getMapsWindow();
    if (!mapsWindow) {
      reject(new Error('Google Places API can only be loaded in the browser.'));
      return;
    }

    const handleLoad = () => {
      waitForPlacesLibraryReady().then(resolve).catch(reject);
    };

    const handleError = () => {
      reject(new Error('Unable to load Google Places script.'));
    };

    const existingScript = document.getElementById(
      GOOGLE_MAPS_SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (existingScript) {
      waitForPlacesLibraryReady().then(resolve).catch(reject);
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.id = GOOGLE_MAPS_SCRIPT_ID;
    scriptElement.async = true;
    scriptElement.defer = true;
    scriptElement.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places`;
    scriptElement.addEventListener('load', handleLoad, { once: true });
    scriptElement.addEventListener('error', handleError, { once: true });
    document.head.appendChild(scriptElement);
  });

  loadPlacesApiPromise = loadPlacesApiPromise.catch((error) => {
    loadPlacesApiPromise = null;
    throw error;
  });

  return loadPlacesApiPromise;
}

function resolveHelperText(
  helperText: string | undefined,
  availabilityState: AvailabilityState,
  translations: ReturnType<typeof getTranslations>['adminInputs']
): string | undefined {
  if (availabilityState === 'missing_api_key') {
    const fallback = translations.googleFallbackDisabled;
    return helperText ? `${helperText} ${fallback}` : fallback;
  }

  if (
    availabilityState === 'script_load_failed' ||
    availabilityState === 'script_missing_library'
  ) {
    const fallback = translations.googleFallbackUnavailable;
    return helperText ? `${helperText} ${fallback}` : fallback;
  }

  return helperText;
}

function isGooglePlacesStatusOk(status: string): boolean {
  const statusOkValue =
    getMapsWindow()?.google?.maps?.places?.PlacesServiceStatus?.OK ?? 'OK';
  return status === statusOkValue || status === 'OK';
}

function isGooglePlacesRequestDenied(status: string): boolean {
  return status.trim().toUpperCase() === 'REQUEST_DENIED';
}

export function GooglePlacesAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  placeholder,
  helperText,
  disabled = false,
  restrictToCities = false,
  restrictToEstablishments = false,
  regionContext = '',
}: GooglePlacesAutocompleteProps) {
  const locale = useLocale();
  const inputTranslations = getTranslations(locale).adminInputs;
  const apiKey = useMemo(
    () => process.env.REACT_APP_WEB_GOOGLE_API_KEY?.trim() ?? '',
    []
  );
  const [availabilityState, setAvailabilityState] = useState<AvailabilityState>(
    apiKey.length ? 'loading' : 'missing_api_key'
  );
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<PlacePredictionOption[]>([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [isSelectionLoading, setIsSelectionLoading] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const autocompleteServiceRef = useRef<GoogleAutocompleteService | null>(null);
  const placesServiceRef = useRef<GooglePlacesService | null>(null);
  const sessionTokenRef = useRef<unknown>(null);
  const skipNextPredictionRequestRef = useRef(false);
  const predictionRequestVersionRef = useRef(0);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!apiKey.length) {
      setAvailabilityState('missing_api_key');
      return;
    }

    let isCancelled = false;
    setAvailabilityState('loading');

    loadGooglePlacesApi(apiKey)
      .then(() => {
        if (isCancelled) {
          return;
        }

        const mapsWindow = getMapsWindow();
        const placesApi = mapsWindow?.google?.maps?.places;
        if (!placesApi?.AutocompleteService) {
          setAvailabilityState('script_missing_library');
          return;
        }

        if (!autocompleteServiceRef.current) {
          autocompleteServiceRef.current = new placesApi.AutocompleteService();
        }
        if (!placesServiceRef.current) {
          const serviceContainer = document.createElement('div');
          placesServiceRef.current = placesApi.PlacesService
            ? new placesApi.PlacesService(serviceContainer)
            : null;
        }
        if (!sessionTokenRef.current && placesApi.AutocompleteSessionToken) {
          sessionTokenRef.current = new placesApi.AutocompleteSessionToken();
        }

        setAvailabilityState('ready');
      })
      .catch(() => {
        if (!isCancelled) {
          setAvailabilityState('script_load_failed');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    const normalizedQuery = inputValue.trim();
    const normalizedRegionContext = regionContext.trim();
    if (!normalizedQuery.length || availabilityState !== 'ready') {
      setOptions([]);
      setIsOptionsOpen(false);
      setIsOptionsLoading(false);
      return;
    }

    if (!isInputFocused) {
      setOptions([]);
      setIsOptionsOpen(false);
      setIsOptionsLoading(false);
      return;
    }

    if (skipNextPredictionRequestRef.current) {
      skipNextPredictionRequestRef.current = false;
      setOptions([]);
      setIsOptionsOpen(false);
      setIsOptionsLoading(false);
      return;
    }

    const autocompleteService = autocompleteServiceRef.current;
    if (!autocompleteService) {
      setOptions([]);
      setIsOptionsOpen(false);
      setIsOptionsLoading(false);
      return;
    }

    let isCancelled = false;
    const requestVersion = predictionRequestVersionRef.current + 1;
    predictionRequestVersionRef.current = requestVersion;
    const timerId = window.setTimeout(() => {
      setIsOptionsLoading(true);

      const request: {
        input: string;
        sessionToken?: unknown;
        types?: string[];
      } = {
        input:
          !restrictToCities && normalizedRegionContext.length > 0
            ? `${normalizedQuery}, ${normalizedRegionContext}`
            : normalizedQuery,
      };

      if (sessionTokenRef.current) {
        request.sessionToken = sessionTokenRef.current;
      }

      if (restrictToCities) {
        request.types = ['(cities)'];
      } else if (restrictToEstablishments) {
        request.types = ['establishment'];
      }

      autocompleteService.getPlacePredictions(
        request,
        (predictions, status) => {
          if (isCancelled) {
            return;
          }

          if (predictionRequestVersionRef.current !== requestVersion) {
            return;
          }

          if (isGooglePlacesRequestDenied(status)) {
            autocompleteServiceRef.current = null;
            placesServiceRef.current = null;
            sessionTokenRef.current = null;
            setAvailabilityState('script_load_failed');
            setOptions([]);
            setIsOptionsOpen(false);
            setIsOptionsLoading(false);
            return;
          }

          if (!isGooglePlacesStatusOk(status) || !predictions?.length) {
            setOptions([]);
            setIsOptionsOpen(false);
            setIsOptionsLoading(false);
            return;
          }

          const filteredPredictions = restrictToCities
            ? predictions.filter((prediction) =>
                prediction.types?.includes('locality')
              )
            : predictions;

          if (!filteredPredictions.length) {
            setOptions([]);
            setIsOptionsOpen(false);
            setIsOptionsLoading(false);
            return;
          }

          const nextOptions = filteredPredictions.map((prediction) => ({
            placeId: prediction.place_id,
            description: prediction.description,
          }));

          setOptions(nextOptions);
          setIsOptionsOpen(true);
          setIsOptionsLoading(false);
        }
      );
    }, 250);

    return () => {
      isCancelled = true;
      window.clearTimeout(timerId);
    };
  }, [
    availabilityState,
    inputValue,
    isInputFocused,
    regionContext,
    restrictToCities,
    restrictToEstablishments,
  ]);

  const resolvedHelperText = resolveHelperText(
    helperText,
    availabilityState,
    inputTranslations
  );

  const fetchPlaceCoordinates = async (
    placeId: string
  ): Promise<{ lat: number; lng: number } | null> => {
    const placesService = placesServiceRef.current;
    if (!placesService) {
      return null;
    }

    return new Promise((resolve) => {
      placesService.getDetails(
        {
          placeId,
          fields: ['geometry'],
          sessionToken: sessionTokenRef.current,
        },
        (place, status) => {
          if (isGooglePlacesRequestDenied(status)) {
            autocompleteServiceRef.current = null;
            placesServiceRef.current = null;
            sessionTokenRef.current = null;
            setAvailabilityState('script_load_failed');
            resolve(null);
            return;
          }

          if (!isGooglePlacesStatusOk(status)) {
            resolve(null);
            return;
          }

          const location = place?.geometry?.location;
          const lat = location?.lat();
          const lng = location?.lng();
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            resolve(null);
            return;
          }

          resolve({
            lat: Number(lat),
            lng: Number(lng),
          });
        }
      );
    });
  };

  if (availabilityState !== 'ready') {
    return (
      <TextField
        fullWidth
        placeholder={placeholder}
        value={inputValue}
        helperText={resolvedHelperText}
        disabled={disabled}
        onFocus={() => setIsInputFocused(true)}
        onBlur={() => {
          predictionRequestVersionRef.current += 1;
          setIsInputFocused(false);
          setIsOptionsOpen(false);
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          setInputValue(nextValue);
          onChange(nextValue);
          onPlaceSelect?.(null);
        }}
      />
    );
  }

  return (
    <Autocomplete<PlacePredictionOption, false, false, true>
      freeSolo
      options={options}
      disabled={disabled}
      loading={isOptionsLoading}
      inputValue={inputValue}
      open={isOptionsOpen && options.length > 0}
      onOpen={() => {
        if (options.length > 0) {
          setIsOptionsOpen(true);
        }
      }}
      onClose={() => {
        predictionRequestVersionRef.current += 1;
        setIsOptionsOpen(false);
      }}
      filterOptions={(providedOptions) => providedOptions}
      isOptionEqualToValue={(option, selectedOption) =>
        typeof selectedOption !== 'string' && option.placeId === selectedOption.placeId
      }
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      onInputChange={(_, nextInputValue, reason) => {
        setInputValue(nextInputValue);

        if (reason === 'input' || reason === 'clear') {
          if (reason === 'input') {
            setIsInputFocused(true);
          }
          onChange(nextInputValue);
          onPlaceSelect?.(null);
        }

        if (reason === 'clear') {
          predictionRequestVersionRef.current += 1;
          setOptions([]);
          setIsOptionsOpen(false);
        }
      }}
      onChange={async (_, nextValue) => {
        if (typeof nextValue === 'string') {
          predictionRequestVersionRef.current += 1;
          setInputValue(nextValue);
          onChange(nextValue);
          onPlaceSelect?.(null);
          return;
        }

        if (!nextValue) {
          predictionRequestVersionRef.current += 1;
          onPlaceSelect?.(null);
          return;
        }

        predictionRequestVersionRef.current += 1;
        skipNextPredictionRequestRef.current = true;
        setOptions([]);
        setIsOptionsOpen(false);
        setInputValue(nextValue.description);
        onChange(nextValue.description);
        setIsSelectionLoading(true);
        const coordinates = await fetchPlaceCoordinates(nextValue.placeId);
        onPlaceSelect?.({
          ...nextValue,
          coordinates,
        });
        setIsSelectionLoading(false);

        const tokenConstructor =
          getMapsWindow()?.google?.maps?.places?.AutocompleteSessionToken;
        if (tokenConstructor) {
          sessionTokenRef.current = new tokenConstructor();
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          placeholder={placeholder}
          helperText={resolvedHelperText}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => {
            setIsInputFocused(false);
            setIsOptionsOpen(false);
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isOptionsLoading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : isSelectionLoading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
